import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload, Loader2, FileWarning, CheckCircle2, Download, Undo2, Eye, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const card = "rounded-2xl bg-white border border-slate-200 p-5";
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const CAT_LABELS = { new: "New", exact_duplicate: "Exact duplicates", conflict: "Possible conflicts", invalid: "Invalid", blank: "Blank/ignored" };
const CAT_COLORS = { new: "text-emerald-700", exact_duplicate: "text-slate-600", conflict: "text-amber-700", invalid: "text-red-600", blank: "text-slate-400" };

const STAGE_LABELS = {
  parsing_file: "Parsing file",
  validating_rows: "Validating rows",
  normalizing_addresses: "Normalising addresses",
  checking_existing_properties: "Checking existing properties",
  checking_campaign_matches: "Checking live campaign matches",
  checking_conflicts: "Checking duplicates & conflicts",
  generating_summary: "Generating summary",
  committing: "Writing properties to the database",
  complete: "Complete",
  failed: "Failed",
};

function RowDetail({ r }) {
  const cw = r.conflict_with;
  const cov = cw?.covered_by_live;
  return (
    <>
      {r.error}
      {cov && (
        <span className="text-emerald-800">
          <b>Covered by live {cov.campaign_code || cov.campaign_name}</b> — existing mailed property: {cov.existing_mailed_address}. Action: skip imported overlap.
        </span>
      )}
      {cw && !cov && cw.type === "range_in_file" && (
        <span className="text-amber-800">Conflict within imported file — overlaps "{cw.existing_address}" (line {cw.line}). Needs review.</span>
      )}
      {cw && !cov && cw.type !== "range_in_file" && (
        <span className="text-amber-800">Genuine unresolved conflict — overlaps existing property {cw.existing_address}. Needs review.</span>
      )}
      {!cw && r.duplicate_of_line && `Exact duplicate of line ${r.duplicate_of_line} in this file`}
      {!cw && !r.duplicate_of_line && r.existing_property_id && "Exact existing property — already in the database"}
      {(r.live_matches || []).map((m) => (
        <span key={m.invite_id} className="block text-cyan-700">↳ exact match in live campaign {m.campaign_code}{m.mailed ? " (mailed — will link, not duplicate)" : ""}{m.lead ? " (lead)" : ""}</span>
      ))}
    </>
  );
}

function RowsTable({ batchId, category, onResolve, refreshNonce }) {
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(1);
  const load = useCallback(() => {
    api.get(`/admin/address-database/import/${batchId}/rows?category=${category}&page=${page}&page_size=100`)
      .then(({ data }) => setRows(data)).catch(() => {});
  }, [batchId, category, page, refreshNonce]);
  useEffect(load, [load]);
  if (!rows) return <Loader2 className="w-4 h-4 animate-spin text-slate-400 my-3" />;
  const canResolve = category === "conflict" || category === "exact_duplicate";
  return (
    <div className="mt-3">
      <table className="w-full text-xs">
        <thead><tr className="text-left text-[10px] uppercase text-slate-500 border-b">
          <th className="py-1.5 pr-2">Line</th><th className="py-1.5 pr-2">Address</th><th className="py-1.5 pr-2">Group</th>
          <th className="py-1.5 pr-2">Detail</th>{canResolve && <th className="py-1.5">Resolution</th>}
        </tr></thead>
        <tbody>
          {rows.rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-100" data-testid={`import-row-${r.id}`}>
              <td className="py-1.5 pr-2 text-slate-400">{r.line}</td>
              <td className="py-1.5 pr-2 font-medium">{r.parsed?.street_address}, {r.parsed?.suburb} {r.parsed?.postcode}</td>
              <td className="py-1.5 pr-2 font-mono">{r.parsed?.campaign_group || "—"}</td>
              <td className="py-1.5 pr-2 text-slate-500"><RowDetail r={r} /></td>
              {canResolve && (
                <td className="py-1.5">
                  <select className="border border-slate-300 rounded px-1 py-0.5 text-xs"
                    value={r.resolution || (r.conflict_with?.covered_by_live ? "covered" : "")}
                    data-testid={`import-resolve-${r.id}`}
                    onChange={(e) => onResolve([r.id], e.target.value).then(load)}>
                    <option value="">— choose —</option>
                    {r.conflict_with?.covered_by_live && <option value="covered">Skip — covered by live campaign (default)</option>}
                    <option value="keep_both">Keep both</option>
                    <option value="use_existing">Use existing</option>
                    <option value="replace_existing">Replace existing</option>
                    <option value="exclude">Exclude row</option>
                    <option value="flag">Flag for later</option>
                  </select>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.pages > 1 && (
        <div className="flex gap-2 mt-2 text-xs text-slate-500 items-center">
          <button className="underline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
          {page}/{rows.pages}
          <button className="underline" disabled={page >= rows.pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}

export default function AddressImport() {
  const [batch, setBatch] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("new");
  const [commitResult, setCommitResult] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [commitPreview, setCommitPreview] = useState(null);
  const [showCommitModal, setShowCommitModal] = useState(false);
  const fileRef = useRef(null);

  const loadHistory = () => api.get("/admin/address-database/import-history").then(({ data }) => setHistory(data.batches)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  // ── background-job polling — survives page refresh (poll by batch id) ──
  const pollRef = useRef(null);
  const stopPolling = () => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };
  useEffect(() => stopPolling, []);

  const startPolling = useCallback((bid) => {
    stopPolling();
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/address-database/import/${bid}`);
        setBatch(data);
        if (data.status === "analyzing" || data.status === "committing") {
          pollRef.current = setTimeout(tick, 2000);
          return;
        }
        loadHistory();
        if (data.status === "analyzed" && !data.commit_result) toast.success("Dry run complete — nothing written yet");
        if (data.status === "committed") { setCommitResult(data.commit_result); toast.success(`Imported ${data.commit_result?.created ?? 0} properties`); }
        if (data.status === "failed") toast.error(`Dry run failed: ${data.analysis?.error || "unknown error"}`);
        if (data.status === "analyzed" && data.analysis?.error) toast.error(`Commit failed: ${data.analysis.error}`);
      } catch { pollRef.current = setTimeout(tick, 4000); }
    };
    tick();
  }, []);

  const openBatch = useCallback(async (bid) => {
    try {
      const { data } = await api.get(`/admin/address-database/import/${bid}`);
      setBatch(data); setCommitResult(data.commit_result || null); setTab("new");
      if (data.status === "analyzing" || data.status === "committing") startPolling(bid);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open batch"); }
  }, [startPolling]);

  // resume viewing a still-running job after browser refresh
  useEffect(() => {
    api.get("/admin/address-database/import-history").then(({ data }) => {
      const running = (data.batches || []).find((b) => b.status === "analyzing" || b.status === "committing");
      if (running) openBatch(running.id);
    }).catch(() => {});
  }, [openBatch]);

  const upload = async (file) => {
    setBusy(true); setCommitResult(null); stopPolling();
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/address-database/import/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch(data);
      loadHistory();
      if (data.duplicate_of_batch) toast.warning(`This file appears to have already been imported on ${fmtDT(data.duplicate_of_batch.uploaded_at)} by ${data.duplicate_of_batch.uploaded_by}`);
      else toast.success(`${data.total_rows} rows uploaded — run the dry run next`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const analyze = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/address-database/import/${batch.id}/analyze`, {});
      setBatch(data);
      toast.info(`Analysing ${data.total_rows} addresses in the background…`);
      startPolling(batch.id);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start dry run"); }
    finally { setBusy(false); }
  };

  const resolve = async (rowIds, resolution) => {
    if (!resolution) return;
    try {
      await api.post(`/admin/address-database/import/${batch.id}/resolve`, { row_ids: rowIds, resolution });
      toast.success("Resolution saved");
      setRefreshNonce((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const bulkResolveCovered = async () => {
    try {
      const { data: cs } = await api.get(`/admin/address-database/import/${batch.id}/covered-summary`);
      if (!window.confirm(
        `Resolve all overlaps in favour of the existing live campaign?\n\n` +
        `Exact live matches (will link, not duplicate): ${cs.exact_live_matches}\n` +
        `Overlap/range matches with mailed recipients: ${cs.overlap_live_matches}\n` +
        `Imported rows that will be skipped: ${cs.will_skip}\n` +
        `Still requiring manual review: ${cs.needs_manual_review}\n\n` +
        `No live campaign recipient, QR token, letter ref, Bing Mail record, lead or mail history is altered.`)) return;
      const { data } = await api.post(`/admin/address-database/import/${batch.id}/resolve-covered`, { confirm: true });
      toast.success(`${data.resolved} overlap(s) resolved — live campaign preserved as authoritative`);
      setRefreshNonce((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk resolve failed"); }
  };

  // explicit pre-commit breakdown — refreshed after any resolution change
  useEffect(() => {
    if (batch?.id && (batch.status === "analyzed" || batch.status === "committed")) {
      api.get(`/admin/address-database/import/${batch.id}/commit-preview`)
        .then(({ data }) => setCommitPreview(data)).catch(() => setCommitPreview(null));
    } else setCommitPreview(null);
  }, [batch?.id, batch?.status, refreshNonce]);

  const commit = async () => {
    setShowCommitModal(false);
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/address-database/import/${batch.id}/commit`, { confirm: true });
      setBatch(data);
      toast.info("Importing in the background…");
      startPolling(batch.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Commit failed"); }
    finally { setBusy(false); }
  };

  const verifyImport = async (bid) => {
    try {
      const { data } = await api.get(`/admin/address-database/import/${bid}/verify`);
      const msg = `Live matches checked: ${data.live_match_rows_checked}\nCorrectly linked: ${data.correctly_linked}\nDuplicate canonical keys: ${data.duplicate_canonical_keys}\nCross-source duplicates: ${data.cross_source_duplicates}\nAnomalies: ${data.anomaly_count}\n\n${data.verdict}`;
      if (data.anomaly_count || data.duplicate_canonical_keys || data.cross_source_duplicates) {
        toast.error(data.verdict);
        window.alert(msg + "\n\n" + JSON.stringify(data.anomalies.slice(0, 5), null, 1));
      } else { toast.success(data.verdict); window.alert(msg); }
    } catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
  };

  const rollback = async (bid) => {
    if (!window.confirm("Roll back this import batch? Only possible when no property has been mailed, assigned to a live campaign, scanned or converted.")) return;
    try {
      const { data } = await api.post(`/admin/address-database/import/${bid}/rollback`);
      if (data.blocked) toast.error(`Rollback blocked — ${data.blocker_count} properties have downstream activity`);
      else { toast.success(`Rolled back — ${data.removed} properties removed`); loadHistory(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Rollback failed"); }
  };

  const discard = async (bid) => {
    if (!window.confirm("Discard this uncommitted upload?")) return;
    try {
      await api.delete(`/admin/address-database/import/${bid}`);
      toast.success("Upload discarded");
      if (batch?.id === bid) { setBatch(null); setCommitResult(null); }
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Discard failed"); }
  };

  const downloadInvalid = () => {
    const token = localStorage.getItem("scwt_token");
    fetch(`${api.defaults.baseURL}/admin/address-database/import/${batch.id}/invalid.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "invalid-rows.csv"; a.click(); });
  };

  const s = batch?.summary;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="address-import-page">
      <Link to="/admin/address-database" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Address Database</Link>
      <h1 className="text-2xl font-bold text-slate-900 mt-2">Import Addresses</h1>
      <p className="text-sm text-slate-500 mt-1">Upload the master spreadsheet (.xlsx or .csv). Everything runs as a dry run first — nothing is written until you explicitly commit, and importing never triggers physical mail.</p>

      <div className={`${card} mt-4`}>
        <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="import-file-input" />
        <div className="flex flex-wrap items-center gap-3">
          <Button disabled={busy} onClick={() => fileRef.current?.click()} className="rounded-full bg-slate-900" data-testid="import-upload-btn">
            <Upload className="w-4 h-4 mr-1" /> {batch ? "Upload different file" : "Choose file"}
          </Button>
          {batch && <div className="text-sm text-slate-600">{batch.filename} · {batch.total_rows} rows · uploaded {fmtDT(batch.uploaded_at)}</div>}
          {batch && (batch.status === "uploaded" || batch.status === "failed") && (
            <Button disabled={busy || batch.missing_columns?.length > 0} onClick={analyze} className="rounded-full bg-cyan-700" data-testid="import-analyze-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : batch.status === "failed" ? <><RefreshCw className="w-4 h-4 mr-1" /> Retry Dry Run</> : "Run Dry Run"}
            </Button>
          )}
        </div>
        {batch && (batch.status === "analyzing" || batch.status === "committing") && (
          <div className="mt-3 rounded-xl bg-cyan-50 border border-cyan-200 p-4" data-testid="import-progress">
            <div className="flex items-center gap-2 text-sm font-semibold text-cyan-900">
              <Loader2 className="w-4 h-4 animate-spin" />
              {batch.status === "analyzing" ? `Analysing ${batch.total_rows?.toLocaleString()} addresses…` : `Importing ${batch.summary?.new?.toLocaleString() || batch.total_rows?.toLocaleString()} addresses…`}
            </div>
            <div className="text-xs text-cyan-800 mt-1" data-testid="import-progress-stage">
              Current stage: {STAGE_LABELS[batch.analysis?.stage] || batch.analysis?.stage || "starting"}
              {batch.analysis?.total ? ` · Progress: ${(batch.analysis.done || 0).toLocaleString()} / ${batch.analysis.total.toLocaleString()}` : ""}
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-cyan-100 overflow-hidden">
              <div className="h-full bg-cyan-600 transition-all" style={{ width: `${Math.min(100, Math.round(((batch.analysis?.done || 0) / (batch.analysis?.total || 1)) * 100))}%` }} />
            </div>
            <div className="text-[11px] text-cyan-700 mt-1.5">You can close this page — processing continues on the server. Reopen it from Import History at any time.</div>
          </div>
        )}
        {batch && (batch.status === "failed" || (batch.status === "analyzed" && batch.analysis?.error)) && (
          <div className="mt-3 rounded-xl bg-red-50 border border-red-300 p-3 text-sm text-red-800" data-testid="import-failed">
            <b>{batch.status === "failed" ? "Dry run failed" : "Last commit attempt failed"}:</b> {batch.analysis?.error || "unknown error"}
            <span className="block text-xs mt-1">Use Retry — no duplicate import batch is created, the same upload is reused.</span>
          </div>
        )}
        {batch?.duplicate_of_batch && (
          <div className="mt-3 rounded-xl bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900" data-testid="import-dup-warning">
            <FileWarning className="inline w-4 h-4 mr-1" /> This file appears to have already been imported — batch {batch.duplicate_of_batch.id.slice(0, 8)} on {fmtDT(batch.duplicate_of_batch.uploaded_at)} by {batch.duplicate_of_batch.uploaded_by} ({batch.duplicate_of_batch.committed_count || 0} rows committed). Proceed only if intentional.
          </div>
        )}
        {batch?.missing_columns?.length > 0 && (
          <div className="mt-3 rounded-xl bg-red-50 border border-red-300 p-3 text-sm text-red-800">
            Missing required column(s): {batch.missing_columns.join(", ")}. Found: {batch.headers.join(", ")}
          </div>
        )}
        {batch && !batch.missing_columns?.length && (
          <div className="mt-3 text-xs text-slate-500">
            Detected columns: {Object.entries(batch.column_mapping).map(([c, h]) => `${h} → ${c}`).join(" · ")}
          </div>
        )}
      </div>

      {s && batch.status !== "analyzing" && batch.status !== "committing" && (
        <div className={`${card} mt-4`} data-testid="import-summary">
          <div className="font-semibold text-slate-900">Dry-run summary — nothing has been written</div>
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-2.5">
            {[["Rows", s.total_rows, "text-slate-900"], ["New", s.new, CAT_COLORS.new], ["Exact matches", s.exact_duplicates, CAT_COLORS.exact_duplicate],
              ["Conflicts", s.conflicts, CAT_COLORS.conflict], ["Invalid", s.invalid, CAT_COLORS.invalid], ["Blank", s.blank_ignored, CAT_COLORS.blank]].map(([l, v, c]) => (
              <div key={l} className="rounded-xl border border-slate-200 p-3">
                <div className={`text-xl font-bold ${c}`}>{v}</div>
                <div className="text-[10px] text-slate-500 font-semibold uppercase">{l}</div>
              </div>
            ))}
          </div>
          {s.live_campaign_matches > 0 && (
            <div className="mt-3 rounded-xl bg-cyan-50 border border-cyan-200 p-3 text-sm text-cyan-900" data-testid="import-live-matches">
              {s.live_campaign_matches} row(s) match addresses in existing live campaigns — their mailed status, QR history and leads will be preserved and linked (no duplicates created).
            </div>
          )}
          {(s.covered_by_live || 0) > 0 && (
            <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-300 p-3 text-sm text-emerald-900" data-testid="import-covered-banner">
              <b>{s.covered_by_live}</b> imported row(s) overlap addresses <b>already mailed in a live campaign</b>. The live campaign is authoritative — these rows default to <b>Skip</b> and need no manual resolution. Nothing in the live campaign is changed.
              <div className="mt-2">
                <Button size="sm" onClick={bulkResolveCovered} className="rounded-full bg-emerald-700" data-testid="import-resolve-covered-btn">
                  Resolve all overlaps in favour of live campaign
                </Button>
              </div>
            </div>
          )}
          {(s.conflicts_needing_review || 0) > 0 && (
            <div className="mt-3 rounded-xl bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900" data-testid="import-needs-review">
              {s.conflicts_needing_review} genuine conflict(s) have no safe live-campaign match and need your review in the Possible conflicts tab.
            </div>
          )}
          <div className="mt-4 flex gap-1.5 flex-wrap">
            {Object.keys(CAT_LABELS).map((c) => (
              <button key={c} onClick={() => setTab(c)} data-testid={`import-tab-${c}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${tab === c ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>
                {CAT_LABELS[c]} ({{ new: s.new, exact_duplicate: s.exact_duplicates, conflict: s.conflicts, invalid: s.invalid, blank: s.blank_ignored }[c]})
              </button>
            ))}
            {s.invalid > 0 && <button onClick={downloadInvalid} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-slate-300 text-slate-600 inline-flex items-center gap-1"><Download className="w-3 h-3" /> Invalid report</button>}
          </div>
          <RowsTable batchId={batch.id} category={tab} onResolve={resolve} refreshNonce={refreshNonce} />
          {(batch.status === "analyzed" || (batch.status === "committed" && commitPreview?.will_commit > 0)) && commitPreview && (
            <div className="mt-4 border-t border-slate-200 pt-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="commit-breakdown">
                <div className="text-sm font-bold text-slate-900 mb-2">What will happen when you import</div>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                  {[
                    ["Total uploaded rows", commitPreview.total_rows, "text-slate-900", "total"],
                    ["Valid rows ready to import", commitPreview.valid_ready, "text-emerald-700 font-bold", "valid"],
                    ["Exact matches linked to existing live properties", commitPreview.live_exact_matches_linked, "text-cyan-700", "live-linked"],
                    ["Covered by live campaign — skipped", commitPreview.covered_by_live_skipped, "text-emerald-700", "covered"],
                    ["Unresolved conflicts remaining", commitPreview.unresolved_conflicts_not_committed, commitPreview.unresolved_conflicts_not_committed ? "text-amber-700 font-bold" : "text-slate-500", "unresolved"],
                    ["Invalid rows", commitPreview.invalid_rows, commitPreview.invalid_rows ? "text-red-600" : "text-slate-500", "invalid"],
                    ["Duplicates within file — skipped", commitPreview.in_file_duplicates_skipped, "text-slate-500", "infile-dups"],
                    ["Blank rows — ignored", commitPreview.blank_ignored, "text-slate-400", "blank"],
                  ].map(([l, v, c, t]) => (
                    <div key={l} className="flex justify-between border-b border-slate-200/70 py-1" data-testid={`commit-line-${t}`}>
                      <span className="text-slate-600">{l}</span><span className={c}>{Number(v).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-4 text-sm">
                  <div className="rounded-lg bg-emerald-100 text-emerald-900 px-3 py-1.5 font-bold" data-testid="commit-will-commit">
                    {Number(commitPreview.will_commit).toLocaleString()} rows WILL be committed
                  </div>
                  <div className="rounded-lg bg-slate-200 text-slate-700 px-3 py-1.5 font-bold" data-testid="commit-will-not-commit">
                    {Number(commitPreview.will_not_commit_total).toLocaleString()} rows will NOT be committed
                    {commitPreview.unresolved_conflicts_not_committed > 0 && ` (incl. ${commitPreview.unresolved_conflicts_not_committed} unresolved conflicts)`}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <Button disabled={busy || commitPreview.will_commit === 0} onClick={() => setShowCommitModal(true)} className="rounded-full bg-emerald-700" data-testid="import-commit-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                    batch.status === "committed"
                      ? `Import ${Number(commitPreview.will_commit).toLocaleString()} Newly Resolved Addresses`
                      : commitPreview.unresolved_conflicts_not_committed > 0
                        ? `Import ${Number(commitPreview.will_commit).toLocaleString()} Valid Addresses and Leave ${Number(commitPreview.unresolved_conflicts_not_committed).toLocaleString()} ${commitPreview.unresolved_conflicts_not_committed === 1 ? "Conflict" : "Conflicts"} Unresolved`
                        : `Import ${Number(commitPreview.will_commit).toLocaleString()} Valid Addresses`}
                </Button>
                <span className="text-xs text-slate-500">No letters or mail are created. Unresolved conflicts are never auto-imported.</span>
              </div>
            </div>
          )}
          {commitResult && (
            <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-300 p-3 text-sm text-emerald-900" data-testid="import-commit-result">
              <CheckCircle2 className="inline w-4 h-4 mr-1" /> Imported {(commitResult.created_new ?? commitResult.created).toLocaleString()} new · linked {((commitResult.linked_live_recipients || 0) + (commitResult.linked_existing || 0)).toLocaleString()} existing{(commitResult.linked_live_recipients || 0) > 0 && ` (${commitResult.linked_live_recipients} live mailed recipients — canonical record created once and linked, no duplicates)`} · updated {commitResult.updated} · covered by live campaign {commitResult.covered_by_live || 0} · skipped {commitResult.skipped}.
              {Object.keys(commitResult.groups || {}).length > 0 && <> Groups: {Object.entries(commitResult.groups).map(([g, n]) => `${g} (${n})`).join(", ")}</>}
            </div>
          )}
        </div>
      )}

      {showCommitModal && commitPreview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowCommitModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="commit-confirm-modal">
            <h3 className="font-bold text-slate-900 text-lg">Confirm Import</h3>
            <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm space-y-1.5">
              <div className="flex justify-between"><span>Rows that will be committed</span><b className="text-emerald-700" data-testid="modal-will-commit">{Number(commitPreview.will_commit).toLocaleString()}</b></div>
              <div className="flex justify-between"><span>Exact matches linked to live properties</span><b className="text-cyan-700">{Number(commitPreview.live_exact_matches_linked).toLocaleString()}</b></div>
              <div className="flex justify-between"><span>Covered by live campaign — skipped</span><b className="text-emerald-700">{Number(commitPreview.covered_by_live_skipped).toLocaleString()}</b></div>
              <div className="flex justify-between"><span>Unresolved conflicts — NOT imported</span><b className="text-amber-700" data-testid="modal-unresolved">{Number(commitPreview.unresolved_conflicts_not_committed).toLocaleString()}</b></div>
              <div className="flex justify-between"><span>Invalid rows — NOT imported</span><b className="text-red-600">{Number(commitPreview.invalid_rows).toLocaleString()}</b></div>
            </div>
            {commitPreview.unresolved_conflicts_not_committed > 0 && (
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900" data-testid="modal-unresolved-note">
                The {Number(commitPreview.unresolved_conflicts_not_committed).toLocaleString()} unresolved conflict(s) will <b>remain unimported</b>. They are never auto-resolved or auto-imported — you can come back and resolve them later from this batch in Import History.
              </div>
            )}
            <div className="mt-3 text-xs text-slate-500">Importing never generates letters, Bing Mail sessions or physical mail, and never changes live campaign recipients.</div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setShowCommitModal(false)} data-testid="modal-cancel-btn">Cancel</Button>
              <Button disabled={busy} onClick={commit} className="rounded-full bg-emerald-700" data-testid="modal-confirm-commit-btn">
                {commitPreview.unresolved_conflicts_not_committed > 0
                  ? `Import ${Number(commitPreview.will_commit).toLocaleString()} and Leave ${Number(commitPreview.unresolved_conflicts_not_committed).toLocaleString()} Unresolved`
                  : `Import ${Number(commitPreview.will_commit).toLocaleString()} Valid Addresses`}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={`${card} mt-6`} data-testid="import-history">
        <div className="font-semibold text-slate-900 mb-3">Import history</div>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase text-slate-500 border-b">
            <th className="py-1.5 pr-2">Date</th><th className="py-1.5 pr-2">File</th><th className="py-1.5 pr-2">By</th>
            <th className="py-1.5 pr-2">Rows</th><th className="py-1.5 pr-2">Committed</th><th className="py-1.5 pr-2">Status</th><th></th>
          </tr></thead>
          <tbody>
            {history.map((b) => (
              <tr key={b.id} className="border-b border-slate-100" data-testid={`import-batch-${b.id}`}>
                <td className="py-1.5 pr-2">{fmtDT(b.uploaded_at)}</td>
                <td className="py-1.5 pr-2 font-medium">{b.filename}</td>
                <td className="py-1.5 pr-2 text-slate-500">{b.uploaded_by}</td>
                <td className="py-1.5 pr-2">{b.total_rows}</td>
                <td className="py-1.5 pr-2">{b.committed_count || 0}</td>
                <td className="py-1.5 pr-2"><span className={`font-semibold ${b.status === "committed" ? "text-emerald-700" : b.status === "rolled_back" || b.status === "failed" ? "text-red-600" : b.status === "analyzing" || b.status === "committing" ? "text-cyan-700" : "text-slate-500"}`}>
                  {(b.status === "analyzing" || b.status === "committing") && <Loader2 className="inline w-3 h-3 animate-spin mr-1" />}{b.status}
                </span></td>
                <td className="py-1.5 space-x-2">
                  <button onClick={() => openBatch(b.id)} className="text-cyan-700 inline-flex items-center gap-1 text-xs" data-testid={`import-view-${b.id}`}>
                    <Eye className="w-3 h-3" /> View
                  </button>
                  {b.status === "committed" && (
                    <>
                      <button onClick={() => verifyImport(b.id)} className="text-emerald-700 text-xs" data-testid={`import-verify-${b.id}`}>
                        Verify
                      </button>
                      <button onClick={() => rollback(b.id)} className="text-red-600 inline-flex items-center gap-1 text-xs" data-testid={`import-rollback-${b.id}`}>
                        <Undo2 className="w-3 h-3" /> Rollback
                      </button>
                    </>
                  )}
                  {(b.status === "uploaded" || b.status === "analyzed" || b.status === "failed") && (
                    <button onClick={() => discard(b.id)} className="text-slate-500 hover:text-red-600 text-xs" data-testid={`import-discard-${b.id}`}>
                      Discard
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!history.length && <tr><td colSpan={7} className="py-4 text-center text-slate-400">No imports yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
